// src/components/Auth/auth.js
import { createContext } from "react";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CookieStorage,
} from "amazon-cognito-identity-js";
import awsmobile from "../../aws-exports";
import { apiFetch } from "../../utils/apiFetch";
import { isLocalHost, isWorkspaceHost, tenantHost } from "../../utils/runtimeConfig";
import { tenantSlug as toTenantSlug } from "../../utils/tenantSlug";

function isAmplifyHost(hostname) {
  return hostname.endsWith(".amplifyapp.com");
}

function getHostTenantSlug() {
  if (typeof window === "undefined") return "";
  const h = (window.location.hostname || "").toLowerCase();

  if (!(h.endsWith(".bimeco.io") && h.split(".").length >= 3)) return "";
  return h.split(".")[0];
}

function readPendingTenantAction() {
  if (typeof window === "undefined") return null;

  const tryParse = (raw) => {
    try {
      const j = JSON.parse(raw);
      if (j && typeof j === "object" && j.tenant) return j;
      return null;
    } catch {
      return null;
    }
  };

  const s1 = sessionStorage.getItem("pendingTenantAction");
  const j1 = s1 ? tryParse(s1) : null;
  if (j1) return j1;

  const s2 = localStorage.getItem("pendingTenantAction");
  const j2 = s2 ? tryParse(s2) : null;
  if (j2) return j2;

  return null;
}

function clearPendingTenantAction() {
  try {
    sessionStorage.removeItem("pendingTenantAction");
    localStorage.removeItem("pendingTenantAction");
  } catch {}
}

export function redirectToTenant(tenantSlug) {
  const hostname = window.location.hostname;
  const port = window.location.port || "8000";

  if (isLocalHost(hostname)) {
    window.location.href = `http://${hostname}:${port}/brands/${tenantSlug}`;
    return;
  }

  if (isAmplifyHost(hostname)) {
    window.location.href = `/brands/${tenantSlug}`;
    return;
  }

  if (isWorkspaceHost(hostname)) {
    const targetHost = tenantHost(tenantSlug);
    if (hostname !== targetHost) {
      window.location.href = `https://${targetHost}/brands/${tenantSlug}`;
    } else {
      window.location.href = `/brands/${tenantSlug}`;
    }
    return;
  }

  window.location.href = `https://${tenantHost(tenantSlug)}/brands/${tenantSlug}`;
}

let cookieConfig = {
  path: "/",
  expires: 365,
  secure: process.env.NODE_ENV !== "development",
  sameSite: "lax",
};

if (typeof window !== "undefined") {
  const h = window.location.hostname.toLowerCase();
  if (h === "bimeco.io" || h.endsWith(".bimeco.io")) {
    cookieConfig.domain = ".bimeco.io";
  }
}

const userPool = new CognitoUserPool({
  UserPoolId: awsmobile.aws_user_pools_id,
  ClientId: awsmobile.aws_user_pools_web_client_id,
  Storage: new CookieStorage(cookieConfig),
});

const AccountContext = createContext();

export function signUp(email, password) {
  return new Promise((resolve, reject) => {
    userPool.signUp(
      email,
      password,
      [{ Name: "email", Value: email }],
      null,
      (err, result) => {
        if (err) {
          console.log("Couldn't sign up");
          reject(err);
          return;
        }
        resolve(result.user);
      }
    );
  });
}

export function confirmSignUp(username, code) {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) {
        console.log("Couldn't verify account");
        reject(err);
        return;
      }
      console.log("Account verified successfully");
      resolve(result);
    });
  });
}

export function signIn(email, password, opts = { redirect: true }) {
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
      Storage: new CookieStorage(cookieConfig),
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: async (result) => {
        cognitoUser.getUserAttributes(async (err, attributes) => {
          if (err) return reject(err);

          const map = {};
          (attributes || []).forEach((a) => (map[a.getName()] = a.getValue()));

          const companyNameRaw =
            map["custom:company"] || map["company"] || map["Company"] || "";

          const tokenTenantSlug = toTenantSlug(companyNameRaw);

          let ensuredTenant = tokenTenantSlug;

          try {
            const r = await apiFetch(`/brand/ensure`, {
              method: "POST",
              headers: { "content-type": "application/json" },
              body: JSON.stringify(
                companyNameRaw?.trim() ? { companyName: companyNameRaw.trim() } : {}
              ),
            });

            const j = await r.json().catch(() => ({}));

            if (r.ok && j?.tenant) ensuredTenant = j.tenant;

            if (r.status === 409 && j?.tenant) ensuredTenant = j.tenant;
          } catch (e) {
            console.warn("brand/ensure failed from auth.js", e);
          }

          const shouldRedirect = opts?.redirect !== false;
          if (shouldRedirect && ensuredTenant) {
            redirectToTenant(ensuredTenant);
            return resolve({ result, tenantSlug: ensuredTenant });
          }

          resolve({ result, tenantSlug: ensuredTenant });
        });
      },
      onFailure: reject,
      newPasswordRequired: reject,
    });
  });
}

export function forgotPassword(username) {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    cognitoUser.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function confirmPassword(username, confirmationCode, newPassword) {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    cognitoUser.confirmPassword(confirmationCode, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function signOut() {
  try {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) cognitoUser.signOut();
  } catch {}

  try {
    localStorage.removeItem("localProvider");
  } catch {}

  try {
    sessionStorage.clear();
  } catch {}

  if (typeof window !== "undefined") {
    hardClearAuthCookies();
  }
}

function hardClearAuthCookies() {
  const hostname = window.location.hostname;

  const domainsToTry = [undefined, hostname, "bimeco.io", ".bimeco.io"];
  const pathsToTry = ["/"];

  const cookieNames = document.cookie
    .split(";")
    .map((c) => c.trim().split("=")[0])
    .filter(Boolean);

  for (const name of cookieNames) {
    for (const domain of domainsToTry) {
      for (const path of pathsToTry) {
        expireCookie(name, { domain, path });
      }
    }
  }
}

function expireCookie(name, opts) {
  const parts = [
    `${encodeURIComponent(name)}=`,
    `expires=${new Date(0).toUTCString()}`,
    "max-age=0",
    `path=${opts.path || "/"}`,
  ];

  if (opts.domain) parts.push(`domain=${opts.domain}`);
  if (window.location.protocol === "https:") parts.push("secure");
  parts.push("samesite=lax");

  document.cookie = parts.join("; ");
}

export async function getCurrentUser() {
  return new Promise((resolve, reject) => {
    const existingUser = userPool.getCurrentUser();

    if (!existingUser) {
      resolve(null);
      return;
    }

    const cognitoUser = new CognitoUser({
      Username: existingUser.getUsername(),
      Pool: userPool,
      Storage: new CookieStorage(cookieConfig),
    });

    cognitoUser.getSession((err) => {
      if (err) {
        reject(err);
        return;
      }

      cognitoUser.getUserAttributes((err, attributes) => {
        if (err) {
          reject(err);
          return;
        }

        const userData = (attributes || []).reduce((acc, attribute) => {
          acc[attribute.getName()] = attribute.getValue();
          return acc;
        }, {});

        resolve({ ...userData, username: cognitoUser.username });
      });
    });
  });
}

export function getSession() {
  const cognitoUser = userPool.getCurrentUser();
  return new Promise((resolve, reject) => {
    if (!cognitoUser) {
      reject(new Error("No user found"));
      return;
    }
    cognitoUser.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(session);
    });
  });
}

export async function getIdTokenJwt() {
  try {
    const session = await getSession();
    return session.getIdToken().getJwtToken();
  } catch {
    return null;
  }
}

export function isLoggedIn() {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(false);
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (err) {
        resolve(false);
        return;
      }
      resolve(session.isValid());
    });
  });
}
